import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Copy, EllipsisVertical, Pencil, RotateCcw, Trash2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { validationMessages, type ActionDef, type InteractionRule, type RegistryKind } from '@/api/actions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/toast'

import { registryPlace, useResetFlow } from './editor/ResetDialog'
import { useDeleteDoc, useDuplicate, useSaveDoc, useToggleEnabled } from './hooks'
import { JsonEditor } from './JsonEditor'
import { EnabledSwitch, OriginBadge } from './parts'

/**
 * The page frame shared by an action's and a world rule's detail: back link, title, origin, the
 * "use it in stories" switch, Edit (the manual editor, `?edit=1`), a ⋯ menu (Duplicate; Delete…
 * for the parent's own, "Reset to original…" for a changed built-in), the body (children), and —
 * folded away — the advanced JSON editor. While editing, the switch and the JSON live in the editor.
 */

type Doc = ActionDef | InteractionRule

export interface DetailFrameProps {
  kind: RegistryKind
  doc: Doc
  title: string
  tag: string | null
  onReload(): void
  /** The manual editor is open (it has its own switch and JSON tab). */
  editing?: boolean
  children: ReactNode
}

export function DetailFrame({ kind, doc, title, tag, onReload, editing = false, children }: DetailFrameProps) {
  const navigate = useNavigate()
  const toggle = useToggleEnabled(kind)
  const save = useSaveDoc(kind)
  const duplicate = useDuplicate(kind)
  const remove = useDeleteDoc(kind)
  const [confirming, setConfirming] = useState(false)
  const tab = kind === 'rule' ? 'rules' : 'actions'
  const noun = kind === 'rule' ? 'world rule' : 'action'
  const builtin = doc.origin === 'builtin'
  const place = registryPlace(doc)
  const reset = useResetFlow(kind, doc.id, title)

  const onDuplicate = (): void =>
    duplicate.mutate(doc, {
      onSuccess: (made) => void navigate({ to: '/actions/$id', params: { id: made.id }, search: { kind } }),
    })

  const onDelete = (): void =>
    remove.mutate(doc.id, {
      onSuccess: () => {
        setConfirming(false)
        toast.success(`Deleted “${title}”`)
        void navigate({ to: '/actions', search: { tab } })
      },
      onError: (err) => toast.error('Could not delete it', { description: validationMessages(err).join(' ') }),
    })

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8" data-testid={`${kind}-detail`}>
      <div className="flex items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/actions" search={{ tab }}>
            <ArrowLeft aria-hidden />
            {kind === 'rule' ? 'All world rules' : 'All actions'}
          </Link>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More">
              <EllipsisVertical aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onDuplicate} disabled={duplicate.isPending}>
              <Copy className="size-4" aria-hidden />
              Duplicate
            </DropdownMenuItem>
            {place === 'changed' ? (
              <DropdownMenuItem onSelect={reset.open}>
                <RotateCcw className="size-4" aria-hidden />
                Reset to original…
              </DropdownMenuItem>
            ) : null}
            {place === 'own' ? (
              <DropdownMenuItem destructive onSelect={() => setConfirming(true)}>
                <Trash2 className="size-4" aria-hidden />
                Delete…
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 max-w-3xl flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <OriginBadge origin={doc.origin} />
            {place === 'changed' ? <Badge tone="accent">Changed by you</Badge> : null}
            {tag ? <Badge tone="neutral">{tag}</Badge> : null}
            {editing ? <Badge tone="info">Editing</Badge> : null}
            <span className="font-mono text-xs text-ink-3" title="Id and revision">
              {doc.id} · rev {doc.rev}
            </span>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-ink">{title}</h1>
          {doc.description ? <p className="text-ink-2">{doc.description}</p> : null}
        </div>
        {editing ? null : (
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-3 rounded-xl border border-line p-3">
              <EnabledSwitch checked={doc.enabled} onChange={(enabled) => toggle.mutate({ id: doc.id, enabled })} disabled={toggle.isPending} label={`Use “${title}” in stories`} />
              <span className="flex flex-col text-sm">
                <span className="font-medium text-ink">{doc.enabled ? 'Used in stories' : 'Switched off'}</span>
                <span className="text-xs text-ink-2">{doc.enabled ? (kind === 'rule' ? 'New stories follow it.' : 'New stories may use it.') : 'New stories leave it out.'}</span>
              </span>
            </label>
            <Button asChild variant="primary" data-testid="edit-doc">
              <Link to="/actions/$id" params={{ id: doc.id }} search={{ kind, edit: 1 }}>
                <Pencil aria-hidden />
                Edit
              </Link>
            </Button>
          </div>
        )}
      </header>

      {children}

      {editing ? null : (
      <details className="card group p-5" data-testid="advanced">
        <summary className="cursor-pointer select-none text-sm font-semibold text-ink">Advanced: edit the {noun} as JSON</summary>
        <div className="mt-4">
          <JsonEditor
            doc={doc as unknown as Record<string, unknown>}
            onSave={(body) => save.mutateAsync({ id: doc.id, baseRev: doc.rev, body })}
            onReload={onReload}
            readOnlyNote={builtin ? `This ${noun} is built in. Saving keeps your version beside the original.` : undefined}
          />
        </div>
      </details>
      )}
      {reset.dialog}

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Delete “{title}”?</DialogTitle>
            <DialogDescription>Stories already made keep playing. New stories can’t use it any more.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={remove.isPending} onClick={onDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** A titled block of the plain-words summary. */
export function SummarySection({ title, children, testId }: { title: string; children: ReactNode; testId?: string }) {
  return (
    <section className="flex flex-col gap-2" data-testid={testId}>
      <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-3">{title}</h2>
      {children}
    </section>
  )
}

export function DetailLoading({ what }: { what: string }) {
  return <div className="grid min-h-[50vh] place-items-center text-ink-2">Opening the {what}…</div>
}

export function DetailMissing({ kind, error }: { kind: RegistryKind; error: unknown }) {
  const tab = kind === 'rule' ? 'rules' : 'actions'
  return (
    <div className="mx-auto flex max-w-xl flex-col items-start gap-4 p-8" role="alert">
      <p className="text-lg text-ink">This {kind === 'rule' ? 'world rule' : 'action'} could not be opened.</p>
      {error ? <p className="text-sm text-ink-2">{validationMessages(error).join(' ')}</p> : null}
      <Button asChild variant="secondary">
        <Link to="/actions" search={{ tab }}>
          {kind === 'rule' ? 'All world rules' : 'All actions'}
        </Link>
      </Button>
    </div>
  )
}
